import { ButtonType, CheckBoxWithLabelWidget, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, FilteringList, ListItem, standardItemFilter } from '@tableau/widgets';
import React from 'react';

import { ReactModalOptions } from '~commonInfra/dialogs/ReactModalService';
import { L10n } from '~src/commonInfra/L10n/L10n';

import { Messages } from '../../../../../codegen/Localize';
import { scimSettingsStyles } from './ScimSettingsStyles';

export interface ScimEnabledSiteDialogProps {
  scimEnabledStates: {
    [site: string]: boolean;
  };
  isScimEnabledFutureSites: boolean;
  updateScimEnabledFutureSiteFlag: (isEnabled: boolean) => void;
  updateSites: (newState: { [site: string]: boolean }) => void;
  updateAll: (state: ScimEnabledSiteDialogState) => void;
  close: () => void;
}

export interface ScimEnabledSiteDialogState {
  scimEnabledStates: ScimEnabledSiteDialogProps['scimEnabledStates'];
  filterText: string;
  isScimEnabledFutureSites: ScimEnabledSiteDialogProps['isScimEnabledFutureSites'];
}

const siteNameToListItem: (site: string) => ListItem = site => ({ value: site, testId: 'site-scim-setting-' + site + '-Item' });

export class ScimEnabledSiteDialog extends React.Component<ScimEnabledSiteDialogProps, ScimEnabledSiteDialogState> {
  public state = {
    scimEnabledStates: this.props.scimEnabledStates,
    filterText: '',
    isScimEnabledFutureSites: this.props.isScimEnabledFutureSites
  };


  render(): React.ReactNode {
    const { filterText, scimEnabledStates } = this.state;

    const siteNames = Object.keys(scimEnabledStates);
    const selectedValues = siteNames.filter(siteName => scimEnabledStates[siteName]);
    const items = siteNames.map(siteNameToListItem);

    const filteredItems = standardItemFilter(items, filterText);
    const siteStates = filteredItems.map(item => scimEnabledStates[item.value]);
    const selectedSiteCount = siteStates.filter(b => b).length;
    const allFilteredSitesChecked = !siteStates.includes(false);
    const allFilteredSitesUnchecked = !siteStates.includes(true);
    const renderedItems = filteredItems.length > 0
      ? filteredItems
      : [{ value: Messages.scimSitesNotFound(), disabled: true }];
    return <>
      <CheckBoxWithLabelWidget
        label={allFilteredSitesChecked
          ? Messages.scimSiteSelectionDialogUncheckAll()
          : Messages.scimSiteSelectionDialogEnableAll()}
        checked={allFilteredSitesChecked}
        indeterminate={!allFilteredSitesChecked && !allFilteredSitesUnchecked}
        disabled={siteStates.length === 0}
        testId="all-sites-scim-setting"
        handleChange={(checked: boolean) => {
          const newState: { [site: string]: boolean } = {};
          filteredItems.forEach(item => {
            const site = item.value;
            newState[site] = checked;
          });
          this.setState({
            scimEnabledStates: {
              ...scimEnabledStates,
              ...newState,
            },
          });
        }}
      />
      <FilteringList
        items={renderedItems}
        style={ scimSettingsStyles.filterList }
        searchText={filterText}
        testId="site-selection-list"
        onFilterChange={filterText => this.setState({ filterText })}
        onSelect={(site: string) => this.setState({
          scimEnabledStates: {
            ...scimEnabledStates,
            [site]: !scimEnabledStates[site],
          }
        })}
        selectedValues={new Set(selectedValues)}
      />
      <div style={ scimSettingsStyles.filterListItem }>
        {Messages.scimServerSettingSitesSelected({
          TOTAL_SITES_COUNT_LOC: L10n.localizedCount(siteStates.length),
          SELECTED_SITES_COUNT_LOC: L10n.localizedCount(selectedSiteCount)
        })}
      </div>
      <CheckBoxWithLabelWidget style={{ margin: '14px 0px 10px 0px' }}
        label= {Messages.enableScimForFutureSites()}
        checked={this.state.isScimEnabledFutureSites}
        testId="enable-scim-future-sites"
        handleChange={(checked: boolean) => {
          this.setState({
            isScimEnabledFutureSites: checked
          });
        }}
      />

      <div>
        <DialogFooterButtonsWidget
          buttons={[{
            handleClick: () => {
              this.props.close();
            },
            buttonType: ButtonType.Outline,
            children: Messages.scimSiteSelectionDialogCancel(),
            testId: 'footer-cancel'
          }, {
            handleClick: () => {
              // Update the site settings
              this.props.updateSites(scimEnabledStates);
              this.props.updateScimEnabledFutureSiteFlag(this.state.isScimEnabledFutureSites);
              this.props.updateAll(this.state);
              this.props.close();
            },
            buttonType: ButtonType.Primary,
            children: Messages.scimSiteSelectionDialogSave(),
            testId: 'footer-save'
          }]}
          testId="footer"
        />
      </div>
    </>;
  }
}

// focus the filter box (instead of the default "Check All" checkbox) when the dialog opens
const findInputToFocus = (dialogElem: HTMLElement) => dialogElem.querySelector<HTMLElement>('input[role="combobox"]');

export function ScimEnabledSiteDialogProps(props: ScimEnabledSiteDialogProps): ReactModalOptions {
  return {
    title: Messages.scimSiteSelectionDialogTitle(),
    testId: 'scim-enabled-site-dialog1',
    type: DialogType.NormalWithTitleBar,
    positioningBehavior: DialogPositioningBehavior.Centered,
    dialogContent: <ScimEnabledSiteDialog {...props} />,
    disableAutoFocus: findInputToFocus,
  };
}
